import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import client from "../api/client";
import "./Admin.css";

/** Paged history of every sync run, with infinite scroll. */

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "running", label: "Running" },
];

const formatTime = (value) => (value ? new Date(value).toLocaleString() : "—");

export default function SyncLogs() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  const requestId = useRef(0);
  const sentinelRef = useRef(null);

  const fetchPage = useCallback(
    async (targetPage, append) => {
      const id = ++requestId.current;
      append ? setLoadingMore(true) : setLoading(true);

      try {
        const { data } = await client.get("/api/drive/sync/logs", {
          params: { page: targetPage, page_size: 25, status: status || undefined },
        });
        if (id !== requestId.current) return;

        setItems((current) => (append ? [...current, ...data.items] : data.items));
        setPage(data.page);
        setTotal(data.total);
        setHasMore(data.has_more);
        setError("");
      } catch (err) {
        if (id !== requestId.current) return;
        console.error("Sync logs failed:", err);
        setError("Could not load sync logs.");
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [status]
  );

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore) {
          fetchPage(page + 1, true);
        }
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, page, loading, loadingMore, fetchPage]);

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <p className="admin-kicker">Google Drive</p>
          <h1>Sync Logs</h1>
        </div>
        <div className="admin-filters">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value || "all"}
              type="button"
              className={filter.value === status ? "active" : ""}
              onClick={() => setStatus(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </header>

      {error && <p className="admin-error" role="alert">{error}</p>}

      {!loading && (
        <p className="admin-count">
          Showing {items.length} of {total} runs
        </p>
      )}

      {loading ? (
        <p>Loading sync logs...</p>
      ) : (
        <>
          <section className="admin-panel">
            <div className="admin-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Trigger</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Added</th>
                    <th>Updated</th>
                    <th>Deleted</th>
                    <th>Skipped</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((run) => (
                    <Fragment key={run.id}>
                      <tr
                        className={run.message ? "is-clickable" : ""}
                        onClick={() =>
                          run.message &&
                          setExpanded((current) =>
                            current === run.id ? null : run.id
                          )
                        }
                      >
                        <td>{formatTime(run.started_at)}</td>
                        <td>{run.trigger}</td>
                        <td>
                          <span className={`pill pill-${run.status}`}>
                            {run.status}
                          </span>
                        </td>
                        <td>
                          {run.duration_seconds != null
                            ? `${run.duration_seconds}s`
                            : "—"}
                        </td>
                        <td>{run.added}</td>
                        <td>{run.updated}</td>
                        <td>{run.deleted}</td>
                        <td>{run.skipped}</td>
                        <td className={run.errors ? "cell-error" : ""}>
                          {run.errors}
                        </td>
                      </tr>
                      {expanded === run.id && run.message && (
                        <tr className="detail-row">
                          <td colSpan={9}>
                            <pre>{run.message}</pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                  {!items.length && (
                    <tr>
                      <td colSpan={9} className="admin-empty-cell">
                        No sync runs recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div ref={sentinelRef} aria-hidden="true" />
          {loadingMore && <p className="admin-count">Loading more...</p>}
        </>
      )}
    </main>
  );
}
